import type { DateTime } from "luxon";
import type { NurseInShift } from "../../utils/models";

type Input = {
	/** The table's location groups in render order. */
	groups: ReadonlyArray<{ locationKey: string; nurses: readonly NurseInShift[] }>;
	/** False for a collapsed group — its rows are unmounted, so it can't hold the anchor. */
	isGroupRendered: (locationKey: string) => boolean;
	/** Whether removing this shift opens a call-off with the replacement search. */
	offersReplacementSearch: (shift: NurseInShift) => boolean;
	now: DateTime;
};

/**
 * The id of the ONE row whose trash icon the call-off walkthrough spotlights: the first rendered row whose
 * removal is a call-off that hasn't started, else the latest-started one. A pending-delete row renders no
 * trash icon, so it is skipped. Null ⇒ no anchor, and the tour is refused rather than pointing at nothing.
 */
export const tableCallOffAnchorShiftId = ({ groups, isGroupRendered, offersReplacementSearch, now }: Input): string | null => {
	const qualifying = groups
		.filter((group) => isGroupRendered(group.locationKey))
		.flatMap((group) => group.nurses)
		.filter((shift) => !shift.isPendingToDelete && offersReplacementSearch(shift));
	// Not-yet-started first, as the launch resolver does: a started shift leaves the window within the
	// after-start allowance, taking the dialog's checkbox with it mid-tour.
	const upcoming = qualifying.find((shift) => shift.startTime > now);
	if (upcoming !== undefined) return upcoming.id;
	// DEFAULT: with nothing upcoming, the latest-started row — it has the most of the after-start window
	// left, and the launch resolver picks its day the same way. Strict `>` keeps the first in render order
	// on a tie.
	const latestStarted = qualifying.reduce<NurseInShift | null>(
		(latest, shift) => (latest === null || shift.startTime > latest.startTime ? shift : latest),
		null,
	);
	return latestStarted?.id ?? null;
};
